import React, { useRef } from 'react';
import { useModal } from '~/hooks/useModal';
import { Alert, Button, FileUploader, Form, Input, Select } from '~/ui/components';
import validateFileName from '~/common/Validations/validateFileName';
import validateFileSize from '~/common/Validations/validateFileSize';
import { AlertContainer, Container, ModalHeader, ModalTitle, CloseButton, Divider } from '../UploadFileModal.styled';
import { findUploadReason, UploadReason, uploadReasons } from '../uploadReasons';

type UploadFileFormProps = {
  onSubmit: (values: any) => void;
  data: {
    uploadReason: {
      name: string;
      value: number;
    } | null;
    comment?: string;
    file: {
      name: string;
      size: number;
    } | null;
  };
};

export const fileSizeMessage = (reason?: Pick<UploadReason, 'minSize' | 'maxSize'>) => {
  if (reason?.minSize && reason?.maxSize) {
    return (
      <>
        O tamanho do arquivo deve ter entre: <b>{reason.minSize}kb</b> e <b>{reason.maxSize}kb</b>
      </>
    );
  }

  if (reason?.minSize) {
    return (
      <>
        O tamanho do arquivo deve ter no mínimo: <b>{reason.minSize}kb</b>
      </>
    );
  }

  if (reason?.maxSize) {
    return (
      <>
        O tamanho do arquivo deve ter no máximo: <b>{reason.maxSize}kb</b>
      </>
    );
  }

  return null;
};

const reasons = uploadReasons.map(({ name, value }) => ({ name, value }));

export function UploadFileForm({ onSubmit, data }: UploadFileFormProps) {
  const formRef = useRef(null);
  const [disabledUpload, setDisabledUpload] = React.useState(!data.uploadReason);
  const [selectedReason, setSelectedReason] = React.useState(findUploadReason(data?.uploadReason?.value));
  const { closeAll } = useModal();

  const closeModal = (e: Event) => {
    e.preventDefault();
    closeAll();
  };

  const onFormChange = (value) => {
    setSelectedReason(findUploadReason(value?.[0]));
    setDisabledUpload(!value.length);
    formRef.current.setFieldsValue({ file: '' });
  };

  return (
    <Container>
      <ModalHeader>
        <ModalTitle>Insira novos Arquivos</ModalTitle>
        <CloseButton width={26} height={26} onClick={closeModal} />
      </ModalHeader>
      <Divider />
      <AlertContainer>
        <Alert status="warning" label="Essa funcionalidade deve ser usada somente por times pré-autorizados." />
      </AlertContainer>
      <Form
        ref={formRef}
        onSubmit={onSubmit}
        inputs={[
          {
            id: 'uploadReason',
            label: 'Motivo do upload',
            initialValue: data?.uploadReason?.value ? [String(data?.uploadReason?.value)] : [],
            input: (
              <Select
                allowClear
                onChange={onFormChange}
                options={reasons}
                fieldNames={{ label: 'name', value: 'value' }}
              />
            ),
            options: {
              rules: [{ required: true, message: 'Este campo é obrigatório' }],
            },
          },
          {
            id: 'comment',
            label: 'Comentário',
            initialValue: data?.comment,
            input: <Input placeholder="Informe o comentário" allowClear textArea rows={3} />,
          },
          {
            id: 'file',
            label: 'Arquivo',
            initialValue: data?.file,
            input: <FileUploader disabled={disabledUpload} accept="application/pdf" />,
            options: {
              rules: [
                { required: true, message: 'Este campo é obrigatório' },
                {
                  validator: validateFileName,
                  allowedName: selectedReason?.fileName,
                  message: (
                    <>
                      O nome do arquivo deve ser: <b>{selectedReason?.fileName}</b>
                    </>
                  ),
                },
                {
                  validator: validateFileSize,
                  min: selectedReason?.minSize,
                  max: selectedReason?.maxSize,
                  message: fileSizeMessage(selectedReason),
                },
              ],
            },
          },
        ]}
      >
        <Button rounded fullWidth>
          Prosseguir
        </Button>
      </Form>
    </Container>
  );
}
